import { isAxiosError } from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AUTH_ROUTES } from '../axios/axiosConfig';
import { getRoleUrlByRoleName } from '../axios/RoleUrl/RoleUrlApi';
import { useAuth } from '../context/AuthContext';
import { useLoading } from '../context/LoadingContext';
import Drawer from '../pages/ControlPanel/Drawer';
import Footer from '../pages/ControlPanel/Footer';
import ControlPanelNavbar from '../pages/ControlPanel/Navbar/ControlPanelNavbar';
import { RefreshProvider } from '../context/RefreshContext';
import { TourProvider, useTour } from "@reactour/tour";
import { advanceTourStep, retreatTourStep } from "../tours/waitForTourElement";

/** Đồng bộ trạng thái tour ra window để Dialog tắt animation khi đang tour */
const TourOpenFlag = () => {
   const { isOpen } = useTour();

   useEffect(() => {
      window.__tourIsOpen = Boolean(isOpen);
      // Tour kết thúc → reset UI tạm (vd. hiện Edit trên nhóm id=1)
      if (!isOpen) {
         window.__hideUserGroupEditActions?.();
         window.__disableTourFakeRoster?.();
         window.__disableTourFakeShift?.();
         window.__closeAddShiftDialog?.();
         window.__closeTourEditRosterDialog?.();
         window.__closeTourDeleteRosterDialog?.();
         window.__closeTourRosterDetail?.();
         window.__closeTourAddUserRoster?.();
         window.__closeTourAddRosterItems?.();
         window.__closeTourAddRequestDialog?.();
         window.__closeTourEditRosterItemDialog?.();
         window.__closeTourDeleteRosterItemDialog?.();
      }
      return () => {
         window.__tourIsOpen = false;
         window.__hideUserGroupEditActions?.();
         window.__disableTourFakeRoster?.();
         window.__disableTourFakeShift?.();
         window.__closeAddShiftDialog?.();
         window.__closeTourEditRosterDialog?.();
         window.__closeTourDeleteRosterDialog?.();
         window.__closeTourRosterDetail?.();
         window.__closeTourAddUserRoster?.();
         window.__closeTourAddRosterItems?.();
         window.__closeTourAddRequestDialog?.();
         window.__closeTourEditRosterItemDialog?.();
         window.__closeTourDeleteRosterItemDialog?.();
      };
   }, [isOpen]);

   return null;
};

const PrivateLayout = () => {
   const { pathname } = useLocation();
   const { profile, isAuthenticated, isNormalLogin } = useAuth();
   const { t } = useTranslation();
   const [show, hide] = useLoading();
   const navigate = useNavigate();
   const [roles, setRoles] = useState([]);
   const [isCollapse, setIsCollapse] = useState(false);
   const [error, setError] = useState();

   const allowRoute = useMemo(
      () =>
         pathname.includes("/controlpanel/company-profile") ||
         pathname.includes("/controlpanel/my-profile") ||
         (roles.length > 0 &&
            roles.some((r) => r.urlPageOfRole.includes(pathname))),
      [roles, pathname]
   );

   useEffect(() => {
      const fetchRoleUrlByRoleName = async (role) => {
         try {
            const { data } = await getRoleUrlByRoleName(
               role,
               localStorage.getItem("i18nextLngId")
            );
            setRoles(data);
            setError(null);
         } catch (error) {
            setError(error);
         }
      };

      if (profile.role) {
         fetchRoleUrlByRoleName(profile.role);
      }
   }, [profile.role]);

   useEffect(() => {
      if (error && isAxiosError(error)) {
         toast.error(t(error.response.data));
      }
   }, [error, t]);

   useEffect(() => {
      if (isAuthenticated && !profile?.companyId) {
         return navigate(
            isNormalLogin
               ? AUTH_ROUTES.signupOrganization
               : AUTH_ROUTES.signupOrganizationSSO
         );
      }
   }, [profile, isAuthenticated, isNormalLogin, navigate]);

   if (!isAuthenticated) {
      return <Navigate to={AUTH_ROUTES.login} />;
   }

   return (
      <TourProvider
         disableInteraction
         steps={[]}
         padding={{ mask: 5, popover: [3, 12] }}
         styles={{
            popover: (base) => ({
               ...base,
               borderRadius: 8,
               maxWidth: 500,
               paddingTop: 20,      // chừa chỗ phía trên
               paddingRight: 36,    // chừa chỗ bên phải, tránh chữ đụng nút X
            }),
            maskArea: (base) => ({ ...base, rx: 10 }),
            close: (base) => ({
               ...base,
               top: 8,              // đẩy nút X lên cao hơn (giảm top = lên trên)
               right: 10,
            }),
            // Nhiều step → xếp chấm đều, căn giữa (tránh space-between làm lệch hàng cuối)
            controls: (base) => ({
               ...base,
               alignItems: "center",
               gap: 8,
            }),
            navigation: (base) => ({
               ...base,
               flex: 1,
               justifyContent: "center",
               flexWrap: "wrap",
               alignItems: "center",
               gap: 6,
               rowGap: 6,
               maxWidth: "100%",
            }),
            dot: (base) => ({
               ...base,
               margin: 0,
               width: 7,
               height: 7,
            }),
         }}
         beforeClose={() => true}
         // Next/Prev: nếu step đích có tourOpen/tourWaitFor thì mở dialog và chờ DOM
         nextButton={({ Button, currentStep, stepsLength, setCurrentStep, steps }) => (
            <Button
               kind="next"
               onClick={() =>
                  advanceTourStep({
                     setCurrentStep,
                     currentStep,
                     stepsLength,
                     steps,
                  })
               }
            />
         )}
         prevButton={({ Button, currentStep, setCurrentStep, steps }) => (
            <Button
               kind="prev"
               onClick={() =>
                  retreatTourStep({
                     setCurrentStep,
                     currentStep,
                     steps,
                  })
               }
            />
         )}
         keyboardHandler={(e, clickProps) => {
            if (!clickProps) return;
            if (e.key === "ArrowRight") {
               e.preventDefault();
               advanceTourStep({
                  setCurrentStep: clickProps.setCurrentStep,
                  currentStep: clickProps.currentStep,
                  stepsLength: clickProps.steps?.length ?? 0,
                  steps: clickProps.steps,
               });
            }
            if (e.key === "ArrowLeft") {
               e.preventDefault();
               retreatTourStep({
                  setCurrentStep: clickProps.setCurrentStep,
                  currentStep: clickProps.currentStep,
                  steps: clickProps.steps,
               });
            }
            if (e.key === "Escape") {
               e.preventDefault();
               clickProps.setIsOpen(false);
            }
         }}
      >
         <TourOpenFlag />
         <div
            className={`flex h-screen w-full bg-[#f9fafb] ${!isCollapse ? "overflow-hidden" : "overflow-hidden"
               }`}
         >
            <Drawer
               isCollapse={isCollapse}
               roles={roles}
               setIsCollapse={setIsCollapse}
            />

            <RefreshProvider>
               <div
                  className={`flex flex-col flex-1 ${!isCollapse ? "min-h-0" : "overflow-hidden"
                     }`}
               >
                  <ControlPanelNavbar
                     isCollapse={isCollapse}
                     allowRoute={allowRoute}
                     setIsCollapse={setIsCollapse}
                  />

                  {/* Content wrapper */}
                  <div
                     className={`flex flex-col flex-1 ${!isCollapse ? "min-h-0 overflow-auto" : "overflow-auto"
                        }`}
                  >
                     <div className={`${!isCollapse ? "flex-1 md:mb-10" : "flex-1 md:mb-10"}`}>
                        {/* Remount trang khi đổi company để load lại dữ liệu */}
                        {allowRoute && <Outlet key={profile.companyId} />}
                     </div>

                     <Footer />
                  </div>
               </div>
            </RefreshProvider>
         </div>
      </TourProvider>
   );
};

export default PrivateLayout;